// E8ThreeNumbersMeanClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 9) – srednia arytmetyczna.
// Srednia x,y = m2 => x+y = 2*m2. Srednia x,y,z = m3 => x+y+z = 3*m3.
// Stad z = 3*m3 - 2*m2.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ m2, m3, fixedOptions = null }) {
  const z = 3 * m3 - 2 * m2;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(z);
  } else {
    const pool = [z, m2, m3, z + 1, z - 1, z + 2];
    const unique = [];
    pool.forEach((v) => {
      if (v > 0 && !unique.includes(v)) unique.push(v);
    });
    const distractors = unique.filter((v) => v !== z).slice(0, 3);
    const ordered = shuffle([z, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(z);
  }

  return { m2, m3, z, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8ThreeNumbersMeanClosedTask",
    data: buildData({ m2: 4, m3: 5, fixedOptions: [4, 5, 6, 7] }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 9,
    },
    tags: ["e8", "statystyka", "srednia-arytmetyczna"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let m2;
  let m3;
  let z;
  do {
    m2 = randInt(3, 8);
    m3 = randInt(4, 9);
    z = 3 * m3 - 2 * m2;
  } while (z <= 0 || z > 14 || z === m2 || z === m3);

  return {
    type: "E8ThreeNumbersMeanClosedTask",
    data: buildData({ m2, m3 }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_9" },
    tags: ["e8", "statystyka", "srednia-arytmetyczna"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Średnia arytmetyczna dwóch liczb <InlineMath math="x" /> i{" "}
        <InlineMath math="y" /> jest równa <InlineMath math={`${data.m2}`} />, a
        średnia arytmetyczna trzech liczb <InlineMath math="x" />,{" "}
        <InlineMath math="y" />, <InlineMath math="z" /> jest równa{" "}
        <InlineMath math={`${data.m3}`} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math="z" /> jest równa
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.z}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const { m2, m3, z } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Ze średniej dwóch liczb:
      </p>
      <BlockMath math={`\\frac{x+y}{2}=${m2}\\ \\Rightarrow\\ x+y=${2 * m2}`} />
      <p>
        <strong>Krok 2.</strong> Ze średniej trzech liczb:
      </p>
      <BlockMath math={`\\frac{x+y+z}{3}=${m3}\\ \\Rightarrow\\ x+y+z=${3 * m3}`} />
      <p>
        <strong>Krok 3.</strong> Odejmujemy:
      </p>
      <BlockMath math={`z=${3 * m3}-${2 * m2}=${z}`} />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
